// In-memory tracking stavu zastavek + Dexie persistence + sync fire-and-forget.
//
// Flow akce (napr. pickup):
//   1. UI klik
//   2. Zapis do Dexie pendingEvents (instant, blokuje pokud Dexie selze)
//   3. Update in-memory state (UI okamzite reaguje)
//   4. fire-and-forget POST /mobile/shift/{id}/event
//      - 2xx -> smaze z pendingEvents
//      - error -> bumpAttempts, event zustane v queue pro sync up (Sprint 4.4)
//
// Stop status flow:
//   waiting -> started -> arrived -> done | futile

import { create } from "zustand";
import type { StopEventIn } from "@routera/shared";
import { api } from "../lib/api";
import { queueEvent, removePendingEvent, bumpAttempts } from "../lib/db";
import { useSyncStore } from "./syncStore";
import { useShiftStore } from "./shiftStore";

export type StopStatus = "waiting" | "started" | "arrived" | "done" | "futile";

/** GPS zastávky přibalené do eventu (traffic collector — sběr reálných jízdních časů). */
export interface StopCoords { lat: number; lng: number; }

/** Vloží lat/lng do payloadu eventu (round na 5 des. míst stačí, souřadnice poboček). */
function withCoords(payload: Record<string, unknown> | undefined, coords?: StopCoords): Record<string, unknown> | undefined {
  if (!coords || typeof coords.lat !== "number" || typeof coords.lng !== "number") return payload;
  return { ...(payload ?? {}), lat: coords.lat, lng: coords.lng };
}

export interface StopProgress {
  status: StopStatus;
  startedAt?: string;
  arrivedAt?: string;
  pickupCount?: number;
  futileReason?: string;
}

interface StopProgressState {
  shiftId: string | null;
  progress: Record<string, StopProgress>;
  hydrated: boolean; // true po prvni uspesne hydrataci z backendu

  setShift: (shiftId: string | null) => void;

  /** Tvrdý reset (přihlášení/odhlášení) — na rozdíl od setShift(null) zahodí
   *  progress i když je shiftId už null. */
  reset: () => void;
  /** Stahne eventy z backendu a rebuilds progress. Volat pri kazdem mount
   * aktivni smeny (= fix bugu, kdy refresh PWA tabu vynulovaval state). */
  hydrateFromBackend: (shiftId: string) => Promise<void>;

  /** coords = GPS zastávky (z plánu) — přibalí se do payloadu pro sběr reálných
   * jízdních časů (traffic collector). Bez coords event funguje dál (fallback na serveru). */
  startStop: (stopId: string, coords?: StopCoords) => Promise<void>;
  arriveStop: (stopId: string, coords?: StopCoords) => Promise<void>;
  /** Krok zpět — smaže poslední status event zastávky na backendu + re-hydratuje. */
  undoLastStep: (stopId: string) => Promise<void>;
  pickup: (stopId: string, count: number, sealNumbers?: string[], stopMeta?: { name?: string; client?: string; address?: string }, materialDelivered?: boolean, coords?: StopCoords) => Promise<void>;
  markFutile: (stopId: string, reason: string) => Promise<void>;
  pinLocation: (stopId: string, lat: number, lng: number, accuracy: number | null) => Promise<void>;
  /** Převzetí ohlášeného odvodu (portál F3) — scénáře ok/swap/chybi/navic + podpisy.
   * Jede přes event log → offline fronta zdarma; server aplikuje na odvod_jbo. */
  odvodEvent: (stopId: string, payload: Record<string, unknown>) => Promise<void>;

  statusOf: (stopId: string) => StopStatus;
  /** Cas dokonceni (HH:MM) pro UI checkmarky v listu. */
  doneAtOf: (stopId: string) => string | undefined;
}

function uuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Persistne event do Dexie + zkusi okamzity sync.
 * Pokud sync failne, event zustane v Dexie pro pozdejsi sync up (Sprint 4.4).
 */
async function persistAndSync(shiftId: string, event: StopEventIn): Promise<void> {
  // 1. Vzdy zapis do Dexie (synchronous pro UI predictability)
  await queueEvent(shiftId, event);
  // Trigger refresh count v badge
  void useSyncStore.getState().refreshCount();

  // 2. Fire-and-forget sync
  try {
    await api.shift.event(shiftId, event);
    await removePendingEvent(event.id);
  } catch (e: any) {
    await bumpAttempts(event.id, e?.message ?? "Unknown error");
    // Don't throw — event stays queued; UI sees success
  } finally {
    void useSyncStore.getState().refreshCount();
  }
}

interface ShiftEvent {
  id: string;
  stop_id: string;
  event_type: string;
  ts: string;
  payload?: { count?: number; reason?: string } | null;
}

/** Reduce event log into per-stop progress map (chronologicky, last wins). */
function reduceEvents(events: ShiftEvent[]): Record<string, StopProgress> {
  const out: Record<string, StopProgress> = {};
  for (const ev of events) {
    const cur = out[ev.stop_id] ?? ({ status: "waiting" } as StopProgress);
    switch (ev.event_type) {
      case "started":
        out[ev.stop_id] = { ...cur, status: "started", startedAt: ev.ts };
        break;
      case "arrived":
        out[ev.stop_id] = { ...cur, status: "arrived", arrivedAt: ev.ts };
        break;
      case "pickup":
        out[ev.stop_id] = {
          ...cur,
          status: "done",
          arrivedAt: cur.arrivedAt ?? ev.ts,
          pickupCount: ev.payload?.count,
        };
        break;
      case "futile":
        out[ev.stop_id] = {
          ...cur,
          status: "futile",
          arrivedAt: cur.arrivedAt ?? ev.ts,
          futileReason: ev.payload?.reason,
        };
        break;
    }
  }
  return out;
}

/** Vrátí shiftId ze store, nebo fallback z shiftStore — pojistka proti race condition při cold startu. */
function _resolveShiftId(stored: string | null): string | null {
  return stored ?? useShiftStore.getState().shift?.id ?? null;
}

function hhmmFromIso(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const useStopProgressStore = create<StopProgressState>((set, get) => ({
  shiftId: null,
  progress: {},
  hydrated: false,

  setShift: (shiftId) =>
    set((s) => {
      // No-op pokud shift_id se nezmenil (predtim resetovalo progress pri kazdem
      // mount StopDetailScreen -> ztrata vsech done/futile stavu!)
      if (s.shiftId === shiftId) return s;
      return { shiftId, progress: {}, hydrated: false };
    }),

  reset: () => set({ shiftId: null, progress: {}, hydrated: false }),

  hydrateFromBackend: async (shiftId) => {
    try {
      const events = (await api.shift.events(shiftId)) as ShiftEvent[];
      const progress = reduceEvents(events);
      set({ shiftId, progress, hydrated: true });
    } catch (e) {
      // Offline / chyba — nezahazuj existujici in-memory progress
      // (uziva se pri restartu se signalem; pri kazde akci se stejne posila up)
      console.warn("[stopProgressStore] hydrate failed:", e);
    }
  },

  startStop: async (stopId, coords) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    const event: StopEventIn = {
      id: uuid(),
      stop_id: stopId,
      event_type: "started",
      ts: nowIso(),
      payload: withCoords(undefined, coords),
    };
    await persistAndSync(shiftId, event);
    set((s) => ({
      progress: { ...s.progress, [stopId]: { ...s.progress[stopId], status: "started", startedAt: event.ts } },
    }));
  },

  arriveStop: async (stopId, coords) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    const ts = nowIso();
    const event: StopEventIn = { id: uuid(), stop_id: stopId, event_type: "arrived", ts, payload: withCoords(undefined, coords) };
    await persistAndSync(shiftId, event);
    set((s) => ({
      progress: { ...s.progress, [stopId]: { ...s.progress[stopId], status: "arrived", arrivedAt: ts } },
    }));
  },

  undoLastStep: async (stopId) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    // Smaže poslední status event na backendu, pak re-hydratuje celý progress
    // (přesně odvodí nový stav ze zbylých eventů — arrivedAt/pickupCount atd.).
    await api.shift.undoLast(shiftId, stopId);
    await get().hydrateFromBackend(shiftId);
  },

  pickup: async (stopId, count, sealNumbers, stopMeta, materialDelivered, coords) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    const payload: Record<string, unknown> = { count };
    if (sealNumbers && sealNumbers.length > 0) payload.seal_numbers = sealNumbers;
    if (stopMeta?.name) payload.name = stopMeta.name;
    if (stopMeta?.client) payload.client = stopMeta.client;
    if (stopMeta?.address) payload.address = stopMeta.address;
    if (materialDelivered) payload.material_delivered = true;
    const event: StopEventIn = {
      id: uuid(),
      stop_id: stopId,
      event_type: "pickup",
      ts: nowIso(),
      payload: withCoords(payload, coords),
    };
    await persistAndSync(shiftId, event);
    set((s) => ({
      progress: { ...s.progress, [stopId]: { ...s.progress[stopId], status: "done", pickupCount: count } },
    }));
  },

  markFutile: async (stopId, reason) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    const event: StopEventIn = {
      id: uuid(),
      stop_id: stopId,
      event_type: "futile",
      ts: nowIso(),
      payload: { reason },
    };
    await persistAndSync(shiftId, event);
    set((s) => ({
      progress: { ...s.progress, [stopId]: { ...s.progress[stopId], status: "futile", futileReason: reason } },
    }));
  },

  odvodEvent: async (stopId, payload) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    const event: StopEventIn = {
      id: uuid(),
      stop_id: stopId,
      event_type: "odvod" as StopEventIn["event_type"],
      ts: nowIso(),
      payload,
    };
    await persistAndSync(shiftId, event);
  },

  pinLocation: async (stopId, lat, lng, accuracy) => {
    const shiftId = _resolveShiftId(get().shiftId);
    if (!shiftId) throw new Error("Smena nebezi");
    const payload: Record<string, number> = { lat, lng };
    if (accuracy != null) payload.accuracy = accuracy;
    const event: StopEventIn = {
      id: uuid(),
      stop_id: stopId,
      event_type: "location_pin",
      ts: nowIso(),
      payload,
    };
    await persistAndSync(shiftId, event);
  },

  statusOf: (stopId) => get().progress[stopId]?.status ?? "waiting",
  doneAtOf: (stopId) => hhmmFromIso(get().progress[stopId]?.arrivedAt),
}));
